/**
 * User validation and status checking utilities
 */

/**
 * Checks if a user account is disabled/inactive
 * @param user - User object to check
 * @returns true if user is disabled, false otherwise
 */
export const isUserDisabled = (user: any): boolean => {
  if (!user) return false
  return user.status === 0 || user.status === "0"
}

/**
 * Checks if a user account is active
 * @param user - User object to check
 * @returns true if user is active, false otherwise
 */
export const isUserActive = (user: any): boolean => {
  if (!user) return false
  return user.status === 1 || user.status === "1"
}

/**
 * Gets user display name with fallback options
 * @param user - User object
 * @returns formatted display name
 */
export const getUserDisplayName = (user: any): string => {
  if (!user) return 'Guest'
  
  if (user.full_name) return user.full_name
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`
  if (user.firstName) return user.firstName
  if (user.name) return user.name
  if (user.email) return user.email
  
  return 'Unknown User'
}

/**
 * Checks if user has specific role
 * @param user - User object
 * @param roleId - Role ID to check
 * @returns true if user has the role
 */
export const userHasRole = (user: any, roleId: number): boolean => {
  if (!user || !user.role_id) return false
  return user.role_id === roleId
}

/**
 * Checks if user has any of the specified roles
 * @param user - User object
 * @param roleIds - Array of role IDs to check
 * @returns true if user has any of the roles
 */
export const userHasAnyRole = (user: any, roleIds: number[]): boolean => {
  if (!user || !user.role_id || !Array.isArray(roleIds)) return false
  return roleIds.includes(user.role_id)
}

/**
 * Gets user role name based on role_id
 * @param user - User object
 * @returns role name string
 */
export const getUserRoleName = (user: any): string => {
  if (!user || !user.role_id) return 'Unknown'
  
  // You can import USER_ROLES constants here if needed
  const roleNames: Record<number, string> = {
    1: 'Admin',
    2: 'Doctor', 
    3: 'Nurse',
    4: 'Patient',
    5: 'Billing Personnel'
  }
  
  return roleNames[user.role_id] || 'Unknown'
}
